import { Link } from "@remix-run/react";
import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Accordion } from "~/components/ui/custom-radix-components/accordion";

import OfficeSearchBanner from "../common/OfficeBanner";
import { DeskAccordionRow } from "../Accordion/DeskAccordionRow";
import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";
import FAQs from "../FAQs/FAQs";
import ManagerCard from "../Card/ManagerCard";
import EBookSection from "../Sections/EBookSection";


import { areaSlug } from "~/lib/helpers/slugHelper";
import BlogsSection from "../Sections/BlogsSection";
import Container from "~/components/atoms/Container";

import ProviderSliderSection from "../Sections/ProviderSliderSection";
import { getCountryName } from "~/lib/helpers/constants";


import { Skeleton } from "~/components/ui/skeleton";
import DesktopHeroBanner from "~/components/blocks/HeroBanners/HeroBannerRoundEdge";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";

const TrendsChart = lazy(() =>
  loadLazyModule(() => import("../Charts/TrendsChart"), EmptyLazyFallback),
);



export default function DesktopLayout({


  data,

  areaSummary,
  faqs,
  nearbyOffices,
  areaBlogs,
  partners,
  trends,
  managers,
  region,

}: {
  data: any
  areaSummary: any
  faqs: any
  nearbyOffices: any
  areaBlogs: any
  partners: any
  trends: any
  managers: any
  region: any


}) {
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadChart, setShouldLoadChart] = useState(false);

  useEffect(() => {
    if (!layoutRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        window.dispatchEvent(
          new CustomEvent("areaDesktopLayoutInView", {
            detail: { inView: entry.isIntersecting },
          }),
        );
      },
      { threshold: 0.05 },
    );
    observer.observe(layoutRef.current);
    return () => observer.disconnect();
  }, []);

  // Chart.js is heavy — only load when the insights block is near the viewport.
  // Heading/copy stay in SSR HTML; the chart mounts as a client enhancement.
  useEffect(() => {
    if (!chartRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadChart(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={layoutRef}>
      <Container htmlFor="fluid" className="pt-8">
        <DesktopHeroBanner
          title={`${areaSummary?.webPrimaryKeyword}`}
          image={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/banners/area-hero.avif`}
          html={areaSummary?.areaHighlights}
          areaPage
          areaSummary={areaSummary}
          config={data.regionData}
        />
      </Container>

      <Container htmlFor="ultra-wide">


        <div className="lg:flex gap-[28px]  z-[0] mt-8">
          <div className=" flex-[5] ">

            <div className="mb-8">
              <FAQs
                title={`Frequently Asked Questions About ${areaSummary.spaceTypeFormula} in ${areaSummary?.city || areaSummary?.suburb}`}
                faqs={faqs}
              />
            </div>
            <div>
              {nearbyOffices?.length ? (
                <div id={PAGE_SECTION_IDS.nearby} className={PAGE_SECTION_SCROLL_CLASS}>
                  <div className="max-w-6xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-2">
                      Compare Average Desk Prices by Area and Team Size in {areaSummary?.city || areaSummary?.suburb}
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      Use this guide to estimate your monthly office expenses by suburb and team size.
                    </p>

                    <div className="overflow-x-auto">
                      <Accordion type="multiple" defaultValue={[]} className="w-full border rounded-lg divide-y p-0">
                        <div className="grid grid-cols-6 bg-gray-100 font-medium text-sm py-3 px-3 pr-8">
                          <div>Location</div>
                          <div>1-5 Desks</div>
                          <div>6-10 Desks</div>
                          <div>11-15 Desks</div>
                          <div>16-25 Desks</div>
                          <div>26-50 Desks</div>
                        </div>

                        {nearbyOffices.map((row: any, index: number) => (
                          <DeskAccordionRow key={index} row={row} index={index} areaSummary={areaSummary} />
                        ))}
                      </Accordion>
                    </div>
                  </div>

                  <section className="my-8">
                    <h2 className="text-2xl font-semibold mb-1">Explore more offices near {areaSummary?.city || areaSummary?.suburb}</h2>
                    <p className="text-muted-foreground mb-6">
                      Here are major business districts in {areaSummary?.city || areaSummary?.suburb} where office spaces are in demand:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px rounded-lg overflow-hidden border">
                      {nearbyOffices.slice(0, 8).map((hub: any, i: number) => {
                        let bgClass;
                        if (i < 4) {
                          bgClass = i % 2 === 0 ? 'bg-gray-50' : 'bg-white';
                        } else {
                          bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                        }

                        return (
                          <Link to={areaSlug(hub)} target="_blank" key={i} className={`${bgClass} p-4 transition h-40`}>
                            <h3 className="font-medium text-lg">{hub.suburb}</h3>
                            <div
                              className="text-sm text-muted-foreground mt-1"
                              dangerouslySetInnerHTML={{
                                __html: `<p style="height:60px;overflow:hidden; text-overflow:ellipsis">${hub.areaDescription}</p>`,
                              }}
                            ></div>
                            <p className="text-sm font-semibold text-gray-600 mt-2">&lt;read more&gt;</p>
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                </div>
              ) : null}
            </div>


            {/* Charts section — heading in SSR; Chart.js only when near viewport */}
            <div id={PAGE_SECTION_IDS.insights} ref={chartRef} className={PAGE_SECTION_SCROLL_CLASS}>
              <div className="rounded-xl border p-4 shadow-sm bg-white my-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-semibold">
                      {areaSummary?.city || areaSummary?.suburb
                        ? `${areaSummary?.city || areaSummary?.suburb} Office Market Insights`
                        : "Office Market Insights"}
                    </h2>
                    <p className="text-sm text-gray-500">
                      10 years of data showing how the coworking industry has grown in{" "}
                      {areaSummary?.city || areaSummary?.suburb}
                    </p>
                  </div>
                </div>
                {shouldLoadChart ? (
                  <Suspense fallback={<Skeleton className="h-64 w-full" />}>
                    <TrendsChart trends={trends} />
                  </Suspense>
                ) : (
                  <Skeleton className="h-64 w-full" />
                )}
              </div>
            </div>


            {areaSummary?.blogPosts.length ?
              <EBookSection areaSummary={areaSummary} /> :
              null
            }

          </div>
          <div id={PAGE_SECTION_IDS.enquire} className={`hidden xl:block xl:sticky top-[100px] xl:h-[calc(100vh-100px)] flex-[2] ${PAGE_SECTION_SCROLL_CLASS}`}>
            <ManagerCard
              managers={managers}
              city={areaSummary?.city || areaSummary?.suburb}
              spaceTypeFormula={areaSummary?.spaceTypeFormula}
            />
          </div>
        </div>

        <div>
          {(() => {
            const providers = Array.isArray(partners)
              ? partners
              : (partners as any)?.data || (partners as any)?.partners || [];
            return Array.isArray(providers) && providers.length ? (
              <div id={PAGE_SECTION_IDS.partners} className={PAGE_SECTION_SCROLL_CLASS}>
              <ProviderSliderSection
                providers={providers as any}
                heading={
                  areaSummary?.city || areaSummary?.suburb
                    ? `Trusted ${areaSummary?.spaceTypeFormula || "Office"} Providers in ${areaSummary?.city || areaSummary?.suburb}`
                    : "Trusted by Recognized Industry Partners"
                }
                description="Top-Rated providers in our Marketplace"
              />
              </div>
            ) : null;
          })()}
          {areaBlogs?.length ? (
            <BlogsSection areaBlogs={areaBlogs} heading={`Latest Blogs & Insights`} region={region} country={getCountryName(region)} />
          ) : null}
        </div>

      </Container>
      <OfficeSearchBanner country={data.params.country} />
    </div >
  );
}


